"use client";

import { useRef } from "react";

import Link from "next/link";

import { ArrowLeft, ArrowUpRight, PhoneCall, WhatsappLogo } from "@phosphor-icons/react";
import { motion, useScroll, useTransform } from "motion/react";

import { SmoothScroll } from "../_components/smooth-scroll";

const easeCubic = [0.32, 0.72, 0, 1] as const;

export default function RequestDemoPage() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const yBackground = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

  return (
    <SmoothScroll>
      <div
        ref={containerRef}
        className="relative min-h-[100dvh] w-full flex flex-col items-center justify-center bg-[#F9F9FB] text-[#0A0A0A] font-sans selection:bg-[#0055FF] selection:text-white overflow-hidden py-24 md:py-40"
        style={{ fontFamily: "var(--font-geist-sans), sans-serif" }}
        dir="ltr"
        lang="en"
      >
        {/* Background Mesh */}
        <motion.div className="absolute inset-0 z-0 pointer-events-none" style={{ y: yBackground }}>
          <div className="absolute top-[-10%] right-[-5%] w-[60vw] h-[80vh] bg-gradient-to-bl from-[#EAEAEF] to-transparent rounded-full blur-[120px] opacity-80" />
          <div className="absolute bottom-[-20%] left-[-10%] w-[50vw] h-[60vh] bg-gradient-to-tr from-[#F4F4F6] to-transparent rounded-full blur-[100px] opacity-60" />
        </motion.div>

        {/* Back Navigation - Fluid Island Pill */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: easeCubic }}
          className="absolute top-8 left-8 z-50"
        >
          <Link
            href="/"
            className="group flex items-center justify-center w-12 h-12 rounded-full bg-white border border-black/5 shadow-sm transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] hover:scale-105 active:scale-95"
            aria-label="Back to Homepage"
          >
            <ArrowLeft weight="light" className="w-5 h-5 text-black/60 group-hover:text-black transition-colors" />
          </Link>
        </motion.div>

        {/* Core Content */}
        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, y: 40, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1.2, ease: easeCubic }}
            className="mb-8 rounded-full bg-black/5 border border-black/5 px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-black/60"
          >
            Priority Access
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 60, filter: "blur(12px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1.4, ease: easeCubic, delay: 0.1 }}
            className="text-5xl md:text-[clamp(3.5rem,5vw,5rem)] leading-[0.9] font-bold tracking-tighter mb-6 text-center text-[#0A0A0A]"
          >
            Connect With <span className="text-black/30">Vanguard.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 40, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1.4, ease: easeCubic, delay: 0.2 }}
            className="text-base sm:text-lg md:text-xl text-black/50 max-w-2xl mb-16 font-medium leading-relaxed text-center text-balance"
            style={{ fontFamily: "var(--font-geist-mono), monospace" }}
          >
            Skip the automated queues. Reach out to our dedicated integration specialists directly via WhatsApp or Phone
            to schedule your personalized academy demonstration.
          </motion.p>

          {/* Double-Bezel Architecture Container */}
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.95, filter: "blur(12px)" }}
            animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
            transition={{ duration: 1.6, ease: easeCubic, delay: 0.3 }}
            className="w-full max-w-2xl rounded-[2.5rem] p-2 bg-black/5 ring-1 ring-black/5 shadow-2xl"
          >
            <div className="rounded-[calc(2.5rem-0.5rem)] bg-white p-8 md:p-12 shadow-[inset_0_1px_1px_rgba(255,255,255,0.8)] border border-black/5 flex flex-col gap-6 relative overflow-hidden">
              {/* Internal abstract decoration */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-green-500/5 to-transparent rounded-full blur-[60px] pointer-events-none" />

              {/* WhatsApp CTA - Button-in-Button Architecture */}
              <Link
                href="https://wa.me/213540839434"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative flex items-center justify-between w-full rounded-[1.5rem] bg-[#0A0A0A] text-white p-2 pl-6 md:pl-8 transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] hover:bg-[#151515] hover:shadow-[0_8px_30px_rgba(0,0,0,0.12)] active:scale-[0.98]"
              >
                <div className="flex items-center gap-4 py-4 md:py-5">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center text-green-400">
                    <WhatsappLogo weight="fill" className="w-6 h-6" />
                  </div>
                  <div className="flex flex-col items-start">
                    <span className="font-semibold text-lg md:text-xl tracking-tight">Message on WhatsApp</span>
                    <span className="text-white/50 text-sm font-medium tracking-wide">Instant response times</span>
                  </div>
                </div>

                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/10 flex items-center justify-center transition-transform duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:bg-green-500 group-hover:text-white group-hover:scale-105 group-hover:-translate-x-[2px] group-hover:-translate-y-[2px]">
                  <ArrowUpRight weight="bold" className="w-5 h-5 md:w-6 md:h-6" />
                </div>
              </Link>

              {/* Direct Call CTA - Button-in-Button Architecture */}
              <Link
                href="tel:+213540839434"
                className="group relative flex items-center justify-between w-full rounded-[1.5rem] bg-black/5 text-[#0A0A0A] p-2 pl-6 md:pl-8 transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] hover:bg-black/10 active:scale-[0.98]"
              >
                <div className="flex items-center gap-4 py-4 md:py-5">
                  <div className="w-10 h-10 rounded-full bg-black/10 flex items-center justify-center text-black/70">
                    <PhoneCall weight="fill" className="w-5 h-5" />
                  </div>
                  <div className="flex flex-col items-start">
                    <span className="font-semibold text-lg md:text-xl tracking-tight text-black/90">
                      Call +213 540839434
                    </span>
                    <span className="text-black/50 text-sm font-medium tracking-wide">
                      Available during business hours
                    </span>
                  </div>
                </div>

                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-black/5 flex items-center justify-center transition-transform duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:bg-[#0055FF] group-hover:text-white group-hover:scale-105 group-hover:-translate-x-[2px] group-hover:-translate-y-[2px]">
                  <ArrowUpRight weight="bold" className="w-5 h-5 md:w-6 md:h-6" />
                </div>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </SmoothScroll>
  );
}
